/* GrowthOS — Campaign form sections (used by addCampaignForm.jsx for Create/Edit
   Campaign). Same config-driven SECTIONS / .gos-cl-* pattern as leadForm.jsx /
   contactForm.jsx / dealForm.jsx — section cards in a 2-col grid + a full-width notes
   card — with Campaign's real field groups (Campaign Setup / Delivery & Audience /
   Description) and required set (campaignName, campaignTypeId, senderEmail,
   campaignFor, templateId, scheduledDateTime — the 6 .required() fields in
   validationSchemaCampaign). Two field types beyond the Lead/Contact text / select /
   textarea contract:
     - `datetime` (Schedule At → <input type="datetime-local">, formatted to/from the
       stored value which the container converts to a UTC ISO string on submit), and
     - a dependent `select` (Template — disabled until Campaign For is chosen, options
       fed in as `templateList` after the container filters them by module).
   The Sender dropdown is populated from REACT_APP_FROM_ADDRESSES (same as the legacy
   form). No searchable field — every dropdown here is a small static list, so the
   local Select is used in plain mode (the search props are left unused). */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveOwners, getGenericMasterFilteredListByKey } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';

const ICONS = {
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  chevD: <path d="M6 9l6 6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
};
const FIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {ICONS[name] || null}
  </svg>
);

/* GrowthOS select (reuses .gos-dd-* dropdown styles); mirrors contactForm.jsx's local
   Select. `searchable`/onSearch/onScroll are supported but unused here (all campaign
   dropdowns are small static lists). `disabled` powers the dependent Template field. */
const Select = ({ value, placeholder, options, onChange, disabled, searchable, onSearch, onScroll, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const onListScroll = (e) => {
    if (!onScroll) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) onScroll();
  };
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" onChange={(e) => onSearch && onSearch(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list" onScroll={onScroll ? onListScroll : undefined}>
            {loading && options.length === 0 ? <div className="gos-dd-empty">Loading…</div> : null}
            {!loading && options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
            {loading && options.length > 0 ? <div className="gos-dd-empty">Loading more…</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// stored value (UTC ISO string on edit-load, or a local input string once edited) →
// the `YYYY-MM-DDTHH:mm` local string a <input type="datetime-local"> expects.
const toLocalInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatEmailToName = (email) =>
  email
    .split('@')[0]
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const CampaignForm = ({ formData, handleChangeForm, handleChange, errormsg, refs, templateList }) => {
  const owner = useSelector(getActiveOwners);
  const campaignType = useSelector(getGenericMasterFilteredListByKey('CampaignType'));

  const emailString = process.env.REACT_APP_FROM_ADDRESSES || '';
  const fromAddressOptions = emailString
    .split(',')
    .filter((email) => email.trim() !== '')
    .map((email) => ({ value: email, label: formatEmailToName(email) }));

  const opt = (list, vKey, lKey, fmt) =>
    (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

  const campaignForChosen = !!formData?.campaignFor;

  // field config: text | select | datetime | textarea
  const SECTIONS = [
    {
      id: 'setup', icon: 'campaigns', title: 'Campaign Setup', sub: 'Name, type & schedule',
      fields: [
        { label: 'Campaign Name', name: 'campaignName', req: true, ref: 'campaignName', full: true },
        { label: 'Type', name: 'campaignTypeId', type: 'select', display: 'campaignType', req: true, ref: 'campaignTypeId', options: opt(campaignType, 'id', 'displayName') },
        { label: 'Campaign Owner', name: 'ownerId', type: 'select', display: 'owner', options: opt(owner, 'userId', 'display_name', NameFormatter) },
        { label: 'Schedule At', name: 'scheduledDateTime', type: 'datetime', req: true, ref: 'scheduledDateTime', full: true },
      ],
    },
    {
      id: 'delivery', icon: 'mail', title: 'Delivery & Audience', sub: 'Who receives it & the template',
      fields: [
        { label: 'Campaign For', name: 'campaignFor', type: 'select', display: 'campaignFor', req: true, ref: 'campaignFor', options: [{ value: 'lead', label: 'Lead' }] },
        {
          label: 'Template', name: 'templateId', type: 'select', display: 'templateName', req: true, ref: 'templateId',
          options: opt(templateList, 'id', 'name'), disabled: !campaignForChosen,
          ph: campaignForChosen ? 'Select Template' : 'Choose Campaign For first',
        },
        { label: 'Sender', name: 'senderEmail', type: 'select', display: 'senderName', req: true, ref: 'senderEmail', options: fromAddressOptions },
      ],
    },
    {
      id: 'notes', icon: 'template', title: 'Description', sub: 'Context for the sales team',
      fields: [{ label: 'Description', name: 'description', type: 'textarea', full: true }],
    },
  ];

  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select') {
      control = (
        <Select
          value={formData?.[fl.display] || ''}
          placeholder={fl.ph || 'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
          disabled={fl.disabled}
        />
      );
    } else if (fl.type === 'datetime') {
      control = (
        <input
          type="datetime-local"
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          value={toLocalInput(formData?.[fl.name])}
          onChange={handleChangeForm}
        />
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          maxLength={250}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name} ref={fl.ref ? refs?.[fl.ref] : undefined}>
        <label className="gos-cl-label">
          {fl.label}
          {fl.req ? <span className="gos-cl-req">*</span> : null}
        </label>
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const secCount = (s) =>
    s.fields.filter((fl) => {
      const v = formData?.[fl.display || fl.name];
      return v !== null && v !== undefined && String(v).trim() !== '';
    }).length;

  const section = (s) => {
    const n = secCount(s);
    const total = s.fields.length;
    return (
      <div className="gos-lr-card gos-cl-sec" id={'sec-' + s.id} key={s.id}>
        <div className="gos-cl-sec-head">
          <span className="gos-cl-sec-ic"><FIc name={s.icon} size={17} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="gos-cl-sec-title">{s.title}</div>
            <div className="gos-cl-sec-sub">{s.sub}</div>
          </div>
          <span className={'gos-cl-count' + (n === total && n > 0 ? ' done' : '')}>{n}/{total}</span>
        </div>
        <div className="gos-cl-sec-body">{s.fields.map(renderField)}</div>
      </div>
    );
  };

  const byId = (id) => SECTIONS.find((s) => s.id === id);

  return (
    <>
      <div className="gos-cl3-grid">
        <div className="gos-cl3-col">{section(byId('setup'))}</div>
        <div className="gos-cl3-col">{section(byId('delivery'))}</div>
      </div>
      <div style={{ marginTop: 18 }}>{section(byId('notes'))}</div>
    </>
  );
};

export default CampaignForm;
